import { useState, useEffect } from 'react';
import { configLoader } from '@/lib/config-loader';
import { MultipleChoice } from './MultipleChoice';
import { TrueFalse } from './TrueFalse';
import { FreeText } from './FreeText';
import { Classification } from './Classification';
import { Matching } from './Matching';
import type { AssessmentQuestion, AssessmentResponse } from '@/lib/types';

interface AssessmentViewerProps {
  moduleId: string;
  partId: string;
  onSubmit: (responses: AssessmentResponse[]) => void;
  onComplete: () => void;
}

export function AssessmentViewer({
  moduleId,
  partId,
  onSubmit,
}: AssessmentViewerProps) {
  const [questions, setQuestions] = useState<AssessmentQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState<Record<string, any>>({});
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    async function loadAssessment() {
      try {
        setLoading(true);
        const assessmentConfig = await configLoader.loadPartAssessment(moduleId, partId);
        setQuestions(assessmentConfig.questions);
      } catch (error) {
        console.error('Failed to load assessment:', error);
      } finally {
        setLoading(false);
      }
    }

    loadAssessment();
  }, [moduleId, partId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="spinner"></div>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="text-center py-20">
        <p className="text-slate-600 dark:text-slate-400">No assessment questions available</p>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === questions.length - 1;

  // For matching questions, check that all items are assigned
  const hasAnswer = (() => {
    const answer = responses[currentQuestion.id];
    if (answer === undefined) return false;
    if (currentQuestion.type === 'matching' && currentQuestion.items) {
      return typeof answer === 'object' && Object.keys(answer).length === currentQuestion.items.length;
    }
    return true;
  })();

  const handleAnswer = (answer: any) => {
    setResponses((prev) => ({
      ...prev,
      [currentQuestion.id]: answer,
    }));
  };

  const handleNext = () => {
    if (!isLastQuestion) {
      setCurrentQuestionIndex((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
    }
  };

  const handleSubmit = () => {
    // Convert responses to AssessmentResponse format
    const assessmentResponses: AssessmentResponse[] = questions.map((q) => ({
      questionId: q.id,
      answer: responses[q.id] || '',
    }));

    setSubmitted(true);
    onSubmit(assessmentResponses);
  };

  const answeredCount = Object.keys(responses).length;
  const allAnswered = answeredCount === questions.length;

  return (
    <div className="max-w-3xl mx-auto">
      {/* Progress bar */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
            Question {currentQuestionIndex + 1} of {questions.length}
          </span>
          <span className="text-sm text-slate-500 dark:text-slate-500">
            {answeredCount} / {questions.length} answered
          </span>
        </div>
        <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
          <div
            className="accent-bg h-2 rounded-full transition-all duration-300"
            style={{ width: `${(answeredCount / questions.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Question */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg p-8 mb-6">
        {currentQuestion.type === 'multiple_choice' && (
          <MultipleChoice
            question={currentQuestion}
            onAnswer={handleAnswer}
            disabled={submitted}
            currentAnswer={responses[currentQuestion.id]}
          />
        )}

        {currentQuestion.type === 'true_false' && (
          <TrueFalse
            question={currentQuestion}
            onAnswer={handleAnswer}
            disabled={submitted}
            currentAnswer={responses[currentQuestion.id]}
          />
        )}

        {currentQuestion.type === 'free_text' && (
          <FreeText
            question={currentQuestion}
            onAnswer={handleAnswer}
            disabled={submitted}
            currentAnswer={responses[currentQuestion.id]}
          />
        )}

        {currentQuestion.type === 'classification' && (
          <Classification
            question={currentQuestion}
            onAnswer={handleAnswer}
            disabled={submitted}
            currentAnswer={responses[currentQuestion.id]}
          />
        )}

        {currentQuestion.type === 'matching' && (
          <Matching
            question={currentQuestion}
            onAnswer={handleAnswer}
            disabled={submitted}
            currentAnswer={responses[currentQuestion.id]}
          />
        )}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={handlePrevious}
          disabled={currentQuestionIndex === 0}
          className="px-6 py-3 rounded-lg font-medium transition-colors
                     disabled:opacity-50 disabled:cursor-not-allowed
                     bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300
                     hover:bg-slate-300 dark:hover:bg-slate-600"
        >
          ← Previous
        </button>

        <div className="flex gap-2">
          {questions.map((q, idx) => (
            <div
              key={q.id}
              className={`
                w-3 h-3 rounded-full transition-all
                ${
                  idx === currentQuestionIndex
                    ? 'accent-bg scale-125'
                    : responses[q.id] !== undefined
                    ? 'bg-green-500'
                    : 'bg-slate-300 dark:bg-slate-600'
                }
              `}
            />
          ))}
        </div>

        {!isLastQuestion ? (
          <button
            onClick={handleNext}
            disabled={!hasAnswer}
            className="px-6 py-3 rounded-lg font-medium transition-colors
                       disabled:opacity-50 disabled:cursor-not-allowed
                       accent-bg hover:opacity-90 text-white"
          >
            Next →
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            disabled={!allAnswered || submitted}
            className="px-6 py-3 rounded-lg font-medium transition-colors
                       disabled:opacity-50 disabled:cursor-not-allowed
                       bg-green-600 hover:bg-green-700 text-white"
          >
            {submitted ? 'Submitted ✓' : 'Submit Assessment'}
          </button>
        )}
      </div>

      {/* Submit prompt */}
      {isLastQuestion && !allAnswered && (
        <p className="text-center text-sm text-amber-600 dark:text-amber-400 mt-4">
          ⚠️ Please answer all questions before submitting
        </p>
      )}
    </div>
  );
}
